import { Card, CardContent, CardHeader } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { CalendarCheck2, MapPinIcon, UserCheck, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Separator } from "@shared/components/ui/separator";
import { STATUS_OPTIONS, STATUS_COLORS } from "@shared/utils/constants";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { PackagesActionsDropdownMenu } from "@/features/packages/components/MenuActions";
import InvoiceBadge from "./InvoiceBadge";
import { useAuth } from '@features/auth/contexts/AuthContext';

export default function PackageCard({ pkg }) {
  const { customer, products, status, updatedAt } = pkg;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  return (
    <Card className="w-full shadow-sm transition-transform duration-300 hover:scale-99 cursor-pointer hover:bg-background/50">
      <CardHeader className="flex flex-row justify-between items-center py-3 ">
        <div className="flex gap-2 items-center justify-between w-full">
          <div className="flex gap-2 items-center">
            <div className="flex items-center text-muted-foreground rounded-md bg-muted/50 px-2 py-1 text-center gap-1">
              <CalendarCheck2 className="h-4 w-4" />
              <span className="text-sm">{format(updatedAt, "dd/MM/yyy")}</span>
            </div>
            <div className="flex items-center text-muted-foreground rounded-md bg-muted/50 px-2 py-1 text-center gap-1">
              <MapPinIcon className="h-4 w-4" />
              <span className="text-sm">{customer.city}</span>
            </div>
          </div>
          <PackagesActionsDropdownMenu pkg={pkg} />
        </div>
      </CardHeader>
      <CardContent
        className="space-x-2 "
        onClick={() => navigate(`/packages/${pkg._id}/view`)}
      >
        <div>
          {/* Livreur Assignment Badge */}
          {pkg.livreur && (
            <div className="flex items-center text-muted-foreground rounded-md bg-blue-50 px-2 py-1 text-center gap-1 w-fit">
              <UserCheck className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-600">
                {pkg.livreur.fullName || pkg.livreur.email}
              </span>
            </div>
          )}

          {/* Show unassigned status for admin */}
          {!pkg.livreur && user?.role === "admin" && (
            <div className="flex items-center text-muted-foreground rounded-md bg-orange-50 px-2 py-1 text-center gap-1 w-fit">
              <User className="h-4 w-4 text-orange-600" />
              <span className="text-sm text-orange-600">{t("Unassigned")}</span>
            </div>
          )}
        </div>
        <div>
          <h2 className="text-xl font-bold mt-1">
            {customer.name} | {customer.phone}
          </h2>
        </div>
        <Separator className="my-4" />
        <div className="flex gap-2 justify-between items-center flex-wrap">
          <div className="flex space-x-2">
            {products.length === 1 ? (
              <Badge key={products[0].name} variant="outline">
                {products[0].name}
                {products[0].quantity > 1 ? ` (x${products[0].quantity})` : ""}
              </Badge>
            ) : (
              <>
                <Badge key={products[0].name} variant="outline">
                  {products[0].name}
                  {products[0].quantity > 1
                    ? ` (x${products[0].quantity})`
                    : ""}
                </Badge>
                <Badge variant="outline">+ {products.length - 1}</Badge>
              </>
            )}
          </div>
          <div className="flex items-center space-x-2 flex-wrap gap-1">
            <InvoiceBadge pkg={pkg} />
            <Badge
              className={`${STATUS_COLORS[status]} hover:${STATUS_COLORS[status]}/80`}
            >
              {t(STATUS_OPTIONS[status])}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
